import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../services/analytics';
import api from '../services/api';

interface PreferencesSetupProps {
  onComplete: () => void;
}

const PreferencesSetup: React.FC<PreferencesSetupProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState({
    notifications: {
      email: true,
      push: false,
      sms: false
    },
    alertTypes: {
      iss: true,
      starlink: true,
      launches: true,
      meteors: false
    },
    viewingConditions: {
      minElevation: 30,
      maxCloudCover: 20,
      timePreference: 'evening' // 'morning', 'evening', 'both'
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleNotificationChange = (type: string) => {
    setPreferences(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: !prev.notifications[type as keyof typeof prev.notifications]
      }
    }));
  };

  const handleAlertTypeChange = (type: string) => {
    setPreferences(prev => ({
      ...prev,
      alertTypes: {
        ...prev.alertTypes,
        [type]: !prev.alertTypes[type as keyof typeof prev.alertTypes]
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Save preferences to backend
      await api.post('/api/preferences', preferences);
      
      trackEvent('preferences_set', JSON.stringify(preferences));
      onComplete();
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save preferences');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    trackEvent('preferences_skipped');
    onComplete();
  };

  return (
    <div className="preferences-setup">
      <style>{`
        .preferences-setup {
          min-height: 100vh;
          background: radial-gradient(circle at center, #0a0e23 0%, #000000 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          position: relative;
          overflow: hidden;
        }

        .preferences-form {
          background: rgba(30, 41, 59, 0.9);
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 16px;
          padding: 3rem;
          max-width: 600px;
          width: 100%;
          backdrop-filter: blur(10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }

        .preferences-title {
          font-size: 2rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.5rem;
          text-align: center;
          text-shadow: 0 0 20px rgba(139, 92, 246, 0.5);
        }

        .preferences-subtitle {
          font-size: 1.1rem;
          color: #94a3b8;
          margin-bottom: 2.5rem;
          text-align: center;
          line-height: 1.5;
        }

        .section {
          margin-bottom: 2rem;
        }

        .section-title {
          font-size: 1.2rem;
          font-weight: 600;
          color: #e2e8f0;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
        }

        .section-title::before {
          content: '';
          width: 4px;
          height: 20px;
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          border-radius: 2px;
          margin-right: 0.75rem;
        }

        .option-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .option-card {
          background: rgba(15, 23, 42, 0.8);
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 8px;
          padding: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }

        .option-card:hover {
          border-color: #8b5cf6;
          background: rgba(139, 92, 246, 0.1);
        }

        .option-card.active {
          border-color: #8b5cf6;
          background: rgba(139, 92, 246, 0.2);
        }

        .option-card.active::after {
          content: '✓';
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          color: #8b5cf6;
          font-weight: bold;
        }

        .option-label {
          font-weight: 600;
          color: #e2e8f0;
          margin-bottom: 0.25rem;
        }

        .option-description {
          font-size: 0.85rem;
          color: #94a3b8;
          line-height: 1.4;
        }

        .slider-group {
          margin-bottom: 1rem;
        }

        .slider-label {
          display: flex;
          justify-content: between;
          align-items: center;
          margin-bottom: 0.5rem;
          color: #e2e8f0;
          font-weight: 500;
        }

        .slider-value {
          color: #8b5cf6;
          font-weight: 600;
        }

        .slider {
          width: 100%;
          height: 6px;
          border-radius: 3px;
          background: rgba(148, 163, 184, 0.3);
          outline: none;
          -webkit-appearance: none;
        }

        .slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #8b5cf6;
          cursor: pointer;
        }

        .time-preference {
          display: flex;
          gap: 0.75rem;
          margin-top: 0.5rem;
        }

        .time-option {
          flex: 1;
          padding: 0.75rem;
          border: 1px solid rgba(148, 163, 184, 0.3);
          border-radius: 8px;
          background: rgba(15, 23, 42, 0.8);
          color: #e2e8f0;
          text-align: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .time-option:hover {
          border-color: #8b5cf6;
        }

        .time-option.active {
          border-color: #8b5cf6;
          background: rgba(139, 92, 246, 0.2);
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
          margin-top: 2rem;
        }

        .skip-button {
          flex: 1;
          padding: 1rem;
          background: transparent;
          color: #94a3b8;
          border: 1px solid rgba(148, 163, 184, 0.3);
          border-radius: 8px;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .skip-button:hover {
          color: #e2e8f0;
          border-color: #94a3b8;
        }

        .save-button {
          flex: 2;
          padding: 1rem;
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .save-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(139, 92, 246, 0.4);
        }

        .save-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .error-message {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          padding: 0.75rem;
          color: #fca5a5;
          font-size: 0.9rem;
          margin-top: 1rem;
        }

        .loading-spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-radius: 50%;
          border-top-color: #ffffff;
          animation: spin 1s ease-in-out infinite;
          margin-right: 0.5rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="preferences-form">
        <h1 className="preferences-title">⚙️ Customize Your Experience</h1>
        <p className="preferences-subtitle">
          Set your preferences to get the most relevant satellite alerts and space updates.
        </p>

        <form onSubmit={handleSubmit}>
          {/* Notification Methods */}
          <div className="section">
            <h3 className="section-title">📧 How do you want to be notified?</h3>
            <div className="option-grid">
              <div
                className={`option-card ${preferences.notifications.email ? 'active' : ''}`}
                onClick={() => handleNotificationChange('email')}
              >
                <div className="option-label">Email Alerts</div>
                <div className="option-description">Get detailed alerts in your inbox</div>
              </div>
              <div
                className={`option-card ${preferences.notifications.push ? 'active' : ''}`}
                onClick={() => handleNotificationChange('push')}
              >
                <div className="option-label">Push Notifications</div>
                <div className="option-description">Quick alerts on your device</div>
              </div>
              <div
                className={`option-card ${preferences.notifications.sms ? 'active' : ''}`}
                onClick={() => handleNotificationChange('sms')}
              >
                <div className="option-label">SMS Alerts</div>
                <div className="option-description">Text messages for urgent events</div>
              </div>
            </div>
          </div>

          {/* Alert Types */}
          <div className="section">
            <h3 className="section-title">🛰️ What interests you most?</h3>
            <div className="option-grid">
              <div
                className={`option-card ${preferences.alertTypes.iss ? 'active' : ''}`}
                onClick={() => handleAlertTypeChange('iss')}
              >
                <div className="option-label">ISS Passes</div>
                <div className="option-description">International Space Station flyovers</div>
              </div>
              <div
                className={`option-card ${preferences.alertTypes.starlink ? 'active' : ''}`}
                onClick={() => handleAlertTypeChange('starlink')}
              >
                <div className="option-label">Starlink Trains</div>
                <div className="option-description">Starlink satellite formations</div>
              </div>
              <div
                className={`option-card ${preferences.alertTypes.launches ? 'active' : ''}`}
                onClick={() => handleAlertTypeChange('launches')}
              >
                <div className="option-label">Rocket Launches</div>
                <div className="option-description">Upcoming space launches</div>
              </div>
              <div
                className={`option-card ${preferences.alertTypes.meteors ? 'active' : ''}`}
                onClick={() => handleAlertTypeChange('meteors')}
              >
                <div className="option-label">Meteor Showers</div>
                <div className="option-description">Peak viewing times for meteors</div>
              </div>
            </div>
          </div>

          {/* Viewing Conditions */}
          <div className="section">
            <h3 className="section-title">👁️ Viewing Preferences</h3>
            
            <div className="slider-group">
              <div className="slider-label">
                <span>Minimum satellite elevation</span>
                <span className="slider-value">{preferences.viewingConditions.minElevation}°</span>
              </div>
              <input
                type="range"
                className="slider"
                min="10"
                max="60"
                value={preferences.viewingConditions.minElevation}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  viewingConditions: {
                    ...prev.viewingConditions,
                    minElevation: parseInt(e.target.value)
                  }
                }))}
              />
            </div>

            <div className="slider-group">
              <div className="slider-label">
                <span>Maximum cloud cover</span>
                <span className="slider-value">{preferences.viewingConditions.maxCloudCover}%</span>
              </div>
              <input
                type="range"
                className="slider"
                min="0"
                max="50"
                value={preferences.viewingConditions.maxCloudCover}
                onChange={(e) => setPreferences(prev => ({
                  ...prev,
                  viewingConditions: {
                    ...prev.viewingConditions,
                    maxCloudCover: parseInt(e.target.value)
                  }
                }))}
              />
            </div>

            <div className="slider-group">
              <div className="slider-label">
                <span>Preferred viewing time</span>
              </div>
              <div className="time-preference">
                <div
                  className={`time-option ${preferences.viewingConditions.timePreference === 'morning' ? 'active' : ''}`}
                  onClick={() => setPreferences(prev => ({
                    ...prev,
                    viewingConditions: { ...prev.viewingConditions, timePreference: 'morning' }
                  }))}
                >
                  🌅 Morning
                </div>
                <div
                  className={`time-option ${preferences.viewingConditions.timePreference === 'evening' ? 'active' : ''}`}
                  onClick={() => setPreferences(prev => ({
                    ...prev,
                    viewingConditions: { ...prev.viewingConditions, timePreference: 'evening' }
                  }))}
                >
                  🌆 Evening
                </div>
                <div
                  className={`time-option ${preferences.viewingConditions.timePreference === 'both' ? 'active' : ''}`}
                  onClick={() => setPreferences(prev => ({
                    ...prev,
                    viewingConditions: { ...prev.viewingConditions, timePreference: 'both' }
                  }))}
                >
                  🌍 Both
                </div>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            <button type="button" className="skip-button" onClick={handleSkip}>
              Skip for now
            </button>
            <button type="submit" className="save-button" disabled={isLoading}>
              {isLoading && <span className="loading-spinner" />}
              {isLoading ? 'Saving...' : 'Save & Continue to Dashboard →'}
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}
        </form>
      </div>
    </div>
  );
};

export default PreferencesSetup;